import React, { useState, useEffect } from 'react';
import { Button } from '@gerdau/hefesto/Button';
import { useHistory } from 'react-router-dom';
import SpreadsheetConfigModal from './SpreadsheetConfigModal';
import { Container, CogIcon, ContentButton } from './styles';
import { RcFile } from 'antd/lib/upload/interface';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { updateLongSteelShoppingCartSettings } from '../../../../../store/ducks/long-steel-shopping-cart/actions';
import { ERoutes } from '../../../../../routes';
import { EPurchaseRoutes } from '../../../purchase.routes';
import { EOrderSettingsRoutes } from '../orderSettings.routes';
import {
  removeSearchedItem,
  searchItemsSpreadsheet
} from '../../../../../store/ducks/long-steel-order-item-search/actions';
import { IOrderEntryPreference } from '../../../../../store/ducks/user/types';
import UploadArea from '../../../../../components/UploadArea/Facelift';
import { ICustomer } from '../../../../../store/ducks/customers/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { Title } from '@gerdau/hefesto/Title';

interface ISpreadsheetUploadProps {
  emitterId: string;
  userOrderEntryPreference: IOrderEntryPreference;
  updateUserOrderEntryPreference(
    userOrderEntryPreference: IOrderEntryPreference
  ): void;
}

const SpreadsheetUpload: React.FC<ISpreadsheetUploadProps> = ({
  emitterId,
  userOrderEntryPreference,
  updateUserOrderEntryPreference
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const customers = useSelector((state: ApplicationState) => state.customers);
  const orderItemSearch = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch
  );
  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const [canShowConfigModal, setCanShowConfigModal] = useState(true);

  const onConfirmSpreadsheetConfig = (
    orderEntryPreference: IOrderEntryPreference,
    hasConfigChanged: boolean
  ) => {
    setCanShowConfigModal(false);

    if (hasConfigChanged) {
      updateUserOrderEntryPreference(orderEntryPreference);
    }
  };

  const onCancelSpreadsheetConfig = () => {
    history.goBack();
  };

  const getCustomer = (): ICustomer => {
    return customers.customers.find(
      (customer) => customer.customerId === emitterId
    ) as ICustomer;
  };

  const onFileUploaded = (file: RcFile) => {
    if (userOrderEntryPreference) {
      dispatch(
        searchItemsSpreadsheet(
          emitterId,
          userOrderEntryPreference,
          file,
          getCustomer().locality.state || ''
        )
      );
    }
  };

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    document.location.pathname
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        page: document.location.pathname,
        screenName,
        event: 'page_view'
      })
    );
  }, []);

  useEffect(() => {
    if (
      orderItemSearch?.searchedItems.length > 0 &&
      !orderItemSearch?.searchedItems.some((item) => !item.found)
    ) {
      // Se ao terminar a busca, e não houver nenhum item NÃO encontrado, manda para o carrinho.
      goToShoppingCart();
    }
  });

  const goToShoppingCart = () => {
    history.push(
      `${ERoutes.PURCHASE}/${EPurchaseRoutes.LONG_STEEL}/${EOrderSettingsRoutes.SHOPPING_CART}`
    );
    const updatedItems = [
      ...shoppingCartState.items,
      ...orderItemSearch.searchedItems
    ];
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        items: updatedItems
      })
    );

    dispatch(removeSearchedItem([]));
  };

  return (
    <Container>
      <ContentButton>
        <Button
          name="configModalButton"
          variant="secondary"
          icon={<CogIcon />}
          onClick={() => {
            dispatch(
              sendTagging({
                screenName,
                event: 'click',
                itemName: 'button:configurar_planilha',
                itemDescription: `${itemDescription}_configurarPlanilha`,
                filterInfo: `#nmrPdd:${
                  shoppingCartState.customerOrder
                    ? shoppingCartState.customerOrder
                    : 'not_set'
                }#codEmr:${
                  shoppingCartState.emitterId
                    ? shoppingCartState.emitterId
                    : 'not_set'
                }#codRbd:${
                  shoppingCartState.receiverId
                    ? shoppingCartState.receiverId
                    : 'not_set'
                }`
              })
            );
            setCanShowConfigModal(true);
          }}
        >
          Configurar Planilha
        </Button>
      </ContentButton>

      <header>
        <Title level={2}>Enviar planilha</Title>
        <span>Envie a planilha com os dados do pedido:</span>
      </header>

      <UploadArea
        acceptedFiles={['.xls, .xlsx']}
        disabled={
          !emitterId ||
          orderItemSearch?.searchingItems ||
          !!customers?.loadingBlocks ||
          !!customers?.loadingProductDescription
        }
        loading={orderItemSearch?.searchingItems}
        acceptMultipleFiles={false}
        onFileListChange={(files: RcFile[]) => {
          if (files.length > 0) {
            onFileUploaded(files[0]);
          }
        }}
        title={'Adicionar planilha'}
        subtitle={
          'Clique ou arraste o arquivo para esta área para adicionar a planilha'
        }
      />

      {userOrderEntryPreference && (
        <SpreadsheetConfigModal
          canShowModal={canShowConfigModal}
          setCanShowModal={setCanShowConfigModal}
          orderEntryPreference={userOrderEntryPreference}
          onCancelClick={onCancelSpreadsheetConfig}
          onConfirmClick={onConfirmSpreadsheetConfig}
        />
      )}
    </Container>
  );
};

export default SpreadsheetUpload;
